import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, User, ArrowLeft } from 'lucide-react';
import { ResponsiveImage } from '../components/ResponsiveImage';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://motoring-daily.onrender.com';

export const ArticleDetailPage = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchArticle = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/articles/${id}`);
      setArticle(data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Article not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-[#00A859] hover:text-[#008a49] font-semibold mb-8 transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="mb-8">
          <span className="bg-[#00A859] text-white text-xs font-bold uppercase tracking-wider py-1 px-3 inline-block mb-4">
            {article.category}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6 font-heading">
            {article.title}
          </h1>
          <div className="flex items-center space-x-4 text-sm text-[#666666]">
            <span className="flex items-center">
              <User className="w-4 h-4 mr-1" />
              {article.author}
            </span>
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {article.read_time} min read
            </span>
            <span>{new Date(article.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        <div className="mb-12">
          <ResponsiveImage
            src={article.image_url}
            alt={article.title}
            className="w-full h-auto object-cover"
            width={1600}
            quality={78}
            eager
          />
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-lg leading-relaxed text-[#1A1A1A] mb-6">
            {article.excerpt}
          </p>
          <div className="text-base leading-relaxed text-[#1A1A1A] whitespace-pre-line">
            {article.content}
          </div>
        </div>
      </div>
    </div>
  );
};